import { URLS } from '../navigation/navigation.data';
import { ProductFeature, ProductPictures, ProductScene, ShowcaseConfig } from './product-features.data';
import type { MoreToolApp } from '../../component/more-tools/more-tools.component';

/** AI Token Meter's raw panels (macos-ai-token-meter/store/shots, the app's demo mode, light), 720px tall on the
 *  page as the Color Pickers' panels, the captures themselves when enlarged. */
export const AI_TOKEN_METER_PICTURES: ProductPictures = { dir: './assets/product-lines/tools/ai-token-meter/', prefix: 'am-' };

const panel = ( key: string, name: string, alt: string ): ProductScene => ( { key, name, alt, width: 390, height: 720 } );

// The App Store pictures' headlines as text, their panels raw (the Apps & Plugins box, as Tidy Desktop's and the
// Color Pickers'). Sold on the site: Add to cart with the price on every slide while the store sells it.
const FEATURES: ProductFeature[] = [
  {
    key: 'today', chip: 'Today', accent: 'Every Token', rest: 'in Your Menu Bar', layout: 'one',
    line: 'Today\'s tokens and what they would cost at API prices, from Claude Code, Codex and Antigravity CLI',
    description: 'Today\'s tokens and what they would cost at API prices, live in the menu bar.',
    scenes: [ panel( 'today', 'Every Token in Your Menu Bar', 'The AI Token Meter panel: today\'s tokens and their cost at API prices, the limits, the sessions working now and this month' ) ],
  },
  {
    key: 'limits', chip: 'Limits', accent: 'Know When', rest: 'Limits Reset', layout: 'one',
    line: 'Codex\'s 5-hour and weekly limits as Codex reports them, and Claude\'s 5-hour window with the time left in it',
    description: 'Claude\'s 5-hour window and Codex\'s own limits at a glance.',
    scenes: [ panel( 'limits', 'Know When Limits Reset', 'AI Token Meter showing Codex only: its 5-hour and weekly limits, today\'s tokens and cost' ) ],
  },
  {
    key: 'models', chip: 'By Model', accent: 'See What', rest: 'Each Model Costs', layout: 'one',
    line: 'This week, this month or all time, by session, by model or by project',
    description: 'Every request priced at its maker\'s own API rates, cache reads and writes included.',
    scenes: [ panel( 'models', 'See What Each Model Costs', 'This month in AI Token Meter by model: Claude, GPT and Gemini models with their tokens and cost' ) ],
  },
  {
    key: 'agents', chip: 'Agents', accent: 'Connect', rest: 'Your AI Agents', layout: 'one',
    line: 'Claude Code, Codex and Antigravity CLI, one click each. Pick the folder and their tokens show up',
    description: 'Connect each agent by choosing its folder in the macOS open panel.',
    scenes: [ { key: 'agents', name: 'Connect Your AI Agents', alt: 'AI Token Meter\'s Agents settings with Claude Code, Codex and Antigravity CLI connected', width: 982, height: 720 } ],
  },
].map( f => ( { ...f, cartSku: 'ai-token-meter/macos' } as ProductFeature ) );

/** The Apps & Plugins box of AI Token Meter (product-showcase.component.ts). */
export const AI_TOKEN_METER_SHOWCASE: ShowcaseConfig = {
  key: 'ai-token-meter',
  shape: 'scene',
  eyebrowLogo: './assets/third-parties/logo/macos-apps.webp',
  eyebrow: 'macOS App',
  icon: './assets/product-lines/tools/ai-token-meter/icon.png',
  title: 'AI Token Meter',
  lead: 'Every token your coding agents use, right in your menu bar',
  text: 'Today\'s tokens from Claude Code, Codex and Antigravity CLI and what they would cost at API prices, their limits and your history by session, model or project. It reads only the token counts in your agents\' own logs, and nothing leaves your Mac.',
  facts: [ 'Native Menu Bar App', 'macOS 14 Or Later', 'Apple Silicon' ],
  link: URLS.plugins.macosAiTokenMeter,
  cta: 'Discover More',
  soon: '',   // sold on codigrate.com only, never on the Mac App Store (owner 2026-10-28)
  pickerLabel: 'What AI Token Meter does',
  pictures: AI_TOKEN_METER_PICTURES,
  slides: FEATURES,
};

/** AI Token Meter under More AI Tools on the AI Cost Calculator (owner 2026-10-22) */
export const AI_TOKEN_METER_MORE_TOOL: MoreToolApp = {
  eyebrow: AI_TOKEN_METER_SHOWCASE.eyebrow,
  icon: AI_TOKEN_METER_SHOWCASE.icon,
  name: AI_TOKEN_METER_SHOWCASE.title,
  lead: AI_TOKEN_METER_SHOWCASE.lead,
  text: AI_TOKEN_METER_SHOWCASE.text,
  facts: AI_TOKEN_METER_SHOWCASE.facts,
  link: AI_TOKEN_METER_SHOWCASE.link,
  cta: AI_TOKEN_METER_SHOWCASE.cta,
  cartSku: 'ai-token-meter/macos',
  soon: AI_TOKEN_METER_SHOWCASE.soon,
  shot: { src: './assets/product-lines/tools/ai-token-meter/am-today.webp', alt: 'The AI Token Meter panel: today\'s tokens and their cost at API prices, the limits and the sessions working now', width: 390, height: 720 },
};
